import React, { useState, useMemo } from 'react';
import {
  ChevronDown,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Info,
  ShieldAlert,
} from 'lucide-react';
import {
  workloadMockData,
  FindingWorkloadItem,
  Urgency,
  Severity,
  ScanType,
  CalendarDay,
  getUrgencyLabel,
  getUrgencyColor,
} from './mocks/workloadMockData';
import { MultiSelectDropdown } from '../../components/common/MultiSelectDropdown';

// ============================================================================
// Summary Card Component
// ============================================================================

interface SummaryCardProps {
  label: string;
  value: number;
  accentColor?: 'red' | 'amber' | 'green' | 'default';
}

const SummaryCard: React.FC<SummaryCardProps> = ({ label, value, accentColor = 'default' }) => {
  const borderColors = {
    red: 'border-l-red-500',
    amber: 'border-l-amber-500',
    green: 'border-l-brand-primary',
    default: '',
  };

  return (
    <div className={`bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4 ${
      accentColor !== 'default' ? `border-l-4 ${borderColors[accentColor]}` : ''
    }`}>
      <p className="text-2xl font-semibold text-ui-text-primary">{value}</p>
      <p className="text-xs text-ui-text-secondary mt-1">{label}</p>
    </div>
  );
};

// ============================================================================
// Severity Badge Component
// ============================================================================

interface SeverityBadgeProps {
  severity: Severity;
}

const SeverityBadge: React.FC<SeverityBadgeProps> = ({ severity }) => {
  const config = {
    critical: { bg: 'bg-red-500/10', text: 'text-red-500', icon: ShieldAlert },
    high: { bg: 'bg-red-500/10', text: 'text-red-500', icon: AlertCircle },
    medium: { bg: 'bg-amber-500/10', text: 'text-amber-500', icon: AlertTriangle },
    low: { bg: 'bg-blue-500/10', text: 'text-blue-500', icon: Info },
  };

  const { bg, text, icon: Icon } = config[severity];

  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${bg} ${text}`}>
      <Icon className="w-3 h-3" />
      {severity.charAt(0).toUpperCase() + severity.slice(1)}
    </span>
  );
};

// ============================================================================
// Scan Type Badge Component
// ============================================================================

interface ScanTypeBadgeProps {
  scanType: ScanType;
}

const ScanTypeBadge: React.FC<ScanTypeBadgeProps> = ({ scanType }) => {
  const labels: Record<ScanType, string> = {
    semgrep: 'Semgrep',
    secrets: 'Secrets',
    deep_ai_scan: 'Deep AI',
  };

  return (
    <span className="text-xs px-2 py-0.5 rounded bg-ui-background-tertiary text-ui-text-secondary">
      {labels[scanType]}
    </span>
  );
};

// ============================================================================
// Calendar Day Row Component
// ============================================================================

interface CalendarDayRowProps {
  day: CalendarDay;
  isSelected: boolean;
  onClick: () => void;
}

const CalendarDayRow: React.FC<CalendarDayRowProps> = ({ day, isSelected, onClick }) => {
  const hasFindings = day.findings.total > 0;
  const hasErrors = day.findings.bySeverity.error > 0;
  const hasWarnings = day.findings.bySeverity.warning > 0;

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 py-2 px-3 rounded-lg transition-colors ${
        day.isToday
          ? 'bg-brand-primary/10 border border-brand-primary'
          : isSelected
            ? 'bg-ui-background-tertiary border border-ui-border-primary'
            : day.isWeekend
              ? 'opacity-50 hover:bg-ui-background-tertiary'
              : 'hover:bg-ui-background-tertiary'
      }`}
    >
      <div className="w-8 text-xs text-ui-text-tertiary">{day.dayOfWeek}</div>
      <div className={`flex-1 text-sm ${day.isToday ? 'font-medium text-brand-primary' : 'text-ui-text-primary'}`}>
        {day.date.slice(8)}
        {day.isToday && <span className="ml-2 text-xs text-brand-primary">Today</span>}
      </div>
      {hasFindings ? (
        <div className="flex items-center gap-1.5">
          <span className={`text-sm font-medium ${day.isPast ? 'text-red-500' : 'text-ui-text-primary'}`}>
            {day.findings.total}
          </span>
          <div className="flex items-center gap-0.5">
            {hasErrors && <span className="w-1.5 h-1.5 rounded-full bg-red-500" />}
            {hasWarnings && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />}
          </div>
        </div>
      ) : (
        <span className="text-xs text-ui-text-tertiary">-</span>
      )}
    </button>
  );
};

// ============================================================================
// Calendar Section Component
// ============================================================================

interface CalendarSectionProps {
  calendar: CalendarDay[];
  bySeverity: typeof workloadMockData.bySeverity;
  selectedDate: string | null;
  onSelectDate: (date: string | null) => void;
}

const CalendarSection: React.FC<CalendarSectionProps> = ({
  calendar,
  bySeverity,
  selectedDate,
  onSelectDate,
}) => {
  return (
    <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4">
      <h3 className="text-sm font-medium text-ui-text-primary mb-4">Deadline Calendar</h3>

      {/* Calendar Days */}
      <div className="space-y-1 mb-6">
        {calendar.map(day => (
          <CalendarDayRow
            key={day.date}
            day={day}
            isSelected={selectedDate === day.date}
            onClick={() => onSelectDate(selectedDate === day.date ? null : day.date)}
          />
        ))}
      </div>

      {/* Severity Breakdown */}
      <div className="pt-4 border-t border-ui-border-primary">
        <h4 className="text-xs font-medium text-ui-text-secondary uppercase tracking-wide mb-3">
          By Severity
        </h4>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-500" />
              <span className="text-ui-text-primary">Error</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-ui-text-primary font-medium">{bySeverity.error.total}</span>
              {bySeverity.error.breached > 0 && (
                <span className="text-xs text-red-500">({bySeverity.error.breached} breached)</span>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <span className="text-ui-text-primary">Warning</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-ui-text-primary font-medium">{bySeverity.warning.total}</span>
              {bySeverity.warning.breached > 0 && (
                <span className="text-xs text-red-500">({bySeverity.warning.breached} breached)</span>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <Info className="w-4 h-4 text-blue-500" />
              <span className="text-ui-text-primary">Info</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-ui-text-primary font-medium">{bySeverity.info.total}</span>
              {bySeverity.info.breached > 0 && (
                <span className="text-xs text-red-500">({bySeverity.info.breached} breached)</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Finding Card Component
// ============================================================================

interface FindingCardProps {
  finding: FindingWorkloadItem;
}

const FindingCard: React.FC<FindingCardProps> = ({ finding }) => {
  const getDaysLabel = (daysRemaining: number) => {
    if (daysRemaining < 0) return `${Math.abs(daysRemaining)} days overdue`;
    if (daysRemaining === 0) return 'Due today';
    if (daysRemaining === 1) return 'Due tomorrow';
    return `${daysRemaining} days remaining`;
  };

  const urgencyColor = getUrgencyColor(finding.urgency);

  return (
    <div className="p-3 rounded-lg border border-ui-border-primary hover:border-brand-primary/50 transition-colors cursor-pointer">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-ui-text-primary truncate">{finding.ruleName}</h4>
          <p className="text-xs text-ui-text-secondary mt-0.5">{finding.applicationName}</p>
          <p className="text-xs text-ui-text-tertiary truncate mt-1">{finding.filePath}</p>
        </div>
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          <SeverityBadge severity={finding.severity} />
          <ScanTypeBadge scanType={finding.scanType} />
        </div>
      </div>
      <div className="flex items-center justify-between mt-3 pt-2 border-t border-ui-border-primary">
        <span className={`text-xs ${urgencyColor.text}`}>
          {getDaysLabel(finding.daysRemaining)}
        </span>
        {finding.assessedPriority && (
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            finding.assessedPriority === 'High'
              ? 'bg-red-500/10 text-red-500'
              : finding.assessedPriority === 'Medium'
                ? 'bg-amber-500/10 text-amber-500'
                : 'bg-blue-500/10 text-blue-500'
          }`}>
            {finding.assessedPriority} Priority
          </span>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Urgency Group Component
// ============================================================================

interface UrgencyGroupProps {
  urgency: Urgency;
  findings: FindingWorkloadItem[];
  isExpanded: boolean;
  onToggle: () => void;
}

const UrgencyGroup: React.FC<UrgencyGroupProps> = ({ urgency, findings, isExpanded, onToggle }) => {
  const urgencyColor = getUrgencyColor(urgency);
  const count = findings.length;

  if (count === 0) return null;

  return (
    <div className="border border-ui-border-primary rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 hover:bg-ui-background-tertiary transition-colors"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-ui-text-tertiary" />
          ) : (
            <ChevronRight className="w-5 h-5 text-ui-text-tertiary" />
          )}
          <span className={`text-sm font-medium ${urgencyColor.text}`}>
            {getUrgencyLabel(urgency)} ({count})
          </span>
        </div>
        <span className={`w-3 h-3 rounded-full ${urgencyColor.bg.replace('/10', '')}`} />
      </button>
      {isExpanded && (
        <div className="p-3 pt-0 space-y-2 bg-ui-background-secondary/30">
          {findings.map(finding => (
            <FindingCard key={finding.id} finding={finding} />
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Findings Section Component
// ============================================================================

interface FindingsSectionProps {
  groupedFindings: Record<Urgency, FindingWorkloadItem[]>;
  expandedGroups: Set<Urgency>;
  onToggleGroup: (urgency: Urgency) => void;
}

const FindingsSection: React.FC<FindingsSectionProps> = ({
  groupedFindings,
  expandedGroups,
  onToggleGroup,
}) => {
  const urgencyOrder: Urgency[] = ['breached', 'due_today', 'due_tomorrow', 'due_this_week', 'healthy'];

  return (
    <div className="space-y-3">
      {urgencyOrder.map(urgency => (
        <UrgencyGroup
          key={urgency}
          urgency={urgency}
          findings={groupedFindings[urgency]}
          isExpanded={expandedGroups.has(urgency)}
          onToggle={() => onToggleGroup(urgency)}
        />
      ))}
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

const ComplianceWorkloadPage: React.FC = () => {
  const data = workloadMockData;

  // State
  const [selectedApps, setSelectedApps] = useState<string[]>([]);
  const [selectedSeverities, setSelectedSeverities] = useState<string[]>([]);
  const [selectedScanTypes, setSelectedScanTypes] = useState<string[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<Urgency>>(
    new Set<Urgency>(['breached', 'due_today'])
  );
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // Filter options
  const appOptions = data.applications.map(app => ({ id: app.id, name: app.name }));
  const severityOptions = [
    { id: 'error', name: 'Error' },
    { id: 'warning', name: 'Warning' },
    { id: 'info', name: 'Info' },
  ];
  const scanTypeOptions = [
    { id: 'semgrep', name: 'Semgrep' },
    { id: 'secrets', name: 'Secrets' },
    { id: 'deep_ai_scan', name: 'Deep AI Scan' },
  ];

  // Filter findings
  const filteredFindings = useMemo(() => {
    return data.findings.filter(f => {
      if (selectedApps.length > 0 && !selectedApps.includes(f.applicationId)) return false;
      if (selectedSeverities.length > 0 && !selectedSeverities.includes(f.severity)) return false;
      if (selectedScanTypes.length > 0 && !selectedScanTypes.includes(f.scanType)) return false;
      if (selectedDate) {
        const deadlineDate = f.slaDeadline.split('T')[0];
        if (deadlineDate !== selectedDate) return false;
      }
      return true;
    });
  }, [data.findings, selectedApps, selectedSeverities, selectedScanTypes, selectedDate]);

  // Group findings by urgency
  const groupedFindings = useMemo(() => {
    const groups: Record<Urgency, FindingWorkloadItem[]> = {
      breached: [],
      due_today: [],
      due_tomorrow: [],
      due_this_week: [],
      healthy: [],
    };
    filteredFindings.forEach(f => groups[f.urgency].push(f));
    return groups;
  }, [filteredFindings]);

  // Compute filtered summary
  const filteredSummary = useMemo(() => {
    const breached = groupedFindings.breached.length;
    const dueToday = groupedFindings.due_today.length;
    const dueTomorrow = groupedFindings.due_tomorrow.length;
    const dueThisWeek = groupedFindings.due_this_week.length;
    const healthy = groupedFindings.healthy.length;
    const totalOpen = filteredFindings.length;
    const dueSoon = dueToday + dueTomorrow + dueThisWeek;

    return { totalOpen, breached, dueSoon, healthy };
  }, [groupedFindings, filteredFindings]);

  // Toggle group handler
  const toggleGroup = (urgency: Urgency) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(urgency)) {
        next.delete(urgency);
      } else {
        next.add(urgency);
      }
      return next;
    });
  };

  // Check if any filters are active
  const hasActiveFilters = selectedApps.length > 0 || selectedSeverities.length > 0 || selectedScanTypes.length > 0 || selectedDate !== null;

  return (
    <div className="min-h-screen bg-ui-background-primary">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-ui-text-primary">Workload</h1>
            <p className="text-sm text-ui-text-secondary mt-1">
              Open findings prioritized by SLA urgency
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <MultiSelectDropdown
              label="Repositories"
              options={appOptions}
              selected={selectedApps}
              onChange={setSelectedApps}
            />
            <MultiSelectDropdown
              label="Severity"
              options={severityOptions}
              selected={selectedSeverities}
              onChange={setSelectedSeverities}
            />
            <MultiSelectDropdown
              label="Scan Type"
              options={scanTypeOptions}
              selected={selectedScanTypes}
              onChange={setSelectedScanTypes}
            />
          </div>
        </div>

        {/* Scope indicator */}
        <div className="text-sm text-ui-text-secondary">
          {data.scope.applicationCount} applications, {data.scope.repositoryCount} repositories
          {hasActiveFilters && (
            <span className="text-ui-text-tertiary ml-2">
              (showing {filteredFindings.length} of {data.findings.length} findings)
            </span>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SummaryCard label="Total Open" value={filteredSummary.totalOpen} />
          <SummaryCard label="Breached" value={filteredSummary.breached} accentColor="red" />
          <SummaryCard label="Due Soon" value={filteredSummary.dueSoon} accentColor="amber" />
          <SummaryCard label="On Track" value={filteredSummary.healthy} accentColor="green" />
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Calendar */}
          <div className="lg:col-span-1">
            <CalendarSection
              calendar={data.calendar}
              bySeverity={data.bySeverity}
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
            />
          </div>

          {/* Right Panel - Findings */}
          <div className="lg:col-span-2">
            <h3 className="text-sm font-medium text-ui-text-primary mb-4">
              Findings by Urgency
              {selectedDate && (
                <button
                  onClick={() => setSelectedDate(null)}
                  className="ml-2 text-xs text-brand-primary hover:underline"
                >
                  Clear date filter
                </button>
              )}
            </h3>
            <FindingsSection
              groupedFindings={groupedFindings}
              expandedGroups={expandedGroups}
              onToggleGroup={toggleGroup}
            />

            {filteredFindings.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-center border border-ui-border-primary rounded-xl">
                <CheckCircle className="w-12 h-12 text-brand-primary mb-3" />
                <p className="text-ui-text-primary font-medium">No findings match your filters</p>
                <p className="text-sm text-ui-text-secondary mt-1">
                  Try adjusting your filter criteria
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceWorkloadPage;
